import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Users,
  UserPlus,
  MessageCircle,
  Send,
  Flame,
  X,
  Check,
  Loader2,
  Edit3,
  UserX,
  Star,
  Clock,
  Globe,
  Target,
  Zap,
  Heart,
  AlertTriangle
} from 'lucide-react';
import { Button, Card, Modal, Textarea, Select, EmptyState } from '../../components/ui';
import { AppLayout } from '../../components/AppLayout';
import { useAuth } from '../../context';
import {
  partnersService,
  PartnerProfileDto,
  PartnershipDto,
  PartnerMessageDto,
  MatchSuggestionDto,
  CreatePartnerProfileRequest
} from '../../services';
import './Partners.css';

// Options for profile form selects
const disciplineOptions = [
  { value: 'music', label: 'Music' },
  { value: 'visual-art', label: 'Visual Art' },
  { value: 'writing', label: 'Writing' },
  { value: 'film', label: 'Film / Video' },
  { value: 'photography', label: 'Photography' },
  { value: 'dance', label: 'Dance' },
  { value: 'theater', label: 'Theater' },
  { value: 'design', label: 'Design' },
  { value: 'software', label: 'Software' },
  { value: 'mixed-media', label: 'Mixed Media' },
  { value: 'other', label: 'Other' },
];

const communicationStyleOptions = [
  { value: 'Async', label: 'Async (Messages & Notes)' },
  { value: 'Sync', label: 'Sync (Live Calls & Chat)' },
  { value: 'Both', label: 'Both (Async + Sync)' },
];

const checkInFrequencyOptions = [
  { value: 'Daily', label: 'Daily' },
  { value: 'Weekly', label: 'Weekly' },
  { value: 'BiWeekly', label: 'Every Other Week' },
];

const experienceLevelOptions = [
  { value: 'Beginner', label: 'Beginner' },
  { value: 'Intermediate', label: 'Intermediate' },
  { value: 'Advanced', label: 'Advanced' },
];

const timezoneOptions = [
  { value: 'UTC-12', label: 'UTC-12' },
  { value: 'UTC-11', label: 'UTC-11' },
  { value: 'UTC-10', label: 'UTC-10 (Hawaii)' },
  { value: 'UTC-9', label: 'UTC-9 (Alaska)' },
  { value: 'UTC-8', label: 'UTC-8 (Pacific)' },
  { value: 'UTC-7', label: 'UTC-7 (Mountain)' },
  { value: 'UTC-6', label: 'UTC-6 (Central)' },
  { value: 'UTC-5', label: 'UTC-5 (Eastern)' },
  { value: 'UTC-4', label: 'UTC-4 (Atlantic)' },
  { value: 'UTC-3', label: 'UTC-3 (Brazil)' },
  { value: 'UTC-2', label: 'UTC-2' },
  { value: 'UTC-1', label: 'UTC-1' },
  { value: 'UTC+0', label: 'UTC+0 (London)' },
  { value: 'UTC+1', label: 'UTC+1 (Berlin)' },
  { value: 'UTC+2', label: 'UTC+2 (Istanbul)' },
  { value: 'UTC+3', label: 'UTC+3 (Moscow)' },
  { value: 'UTC+4', label: 'UTC+4 (Dubai)' },
  { value: 'UTC+5', label: 'UTC+5' },
  { value: 'UTC+5:30', label: 'UTC+5:30 (Mumbai)' },
  { value: 'UTC+6', label: 'UTC+6' },
  { value: 'UTC+7', label: 'UTC+7 (Bangkok)' },
  { value: 'UTC+8', label: 'UTC+8 (Singapore)' },
  { value: 'UTC+9', label: 'UTC+9 (Tokyo)' },
  { value: 'UTC+10', label: 'UTC+10 (Sydney)' },
  { value: 'UTC+11', label: 'UTC+11' },
  { value: 'UTC+12', label: 'UTC+12 (Auckland)' },
];

const goalOptions = [
  'Finish an album',
  'Daily creative practice',
  'Learn new skills',
  'Build a portfolio',
  'Overcome creative block',
  'Stay consistent',
  'Network & collaborate',
  'Launch a project',
];

type TabView = 'profile' | 'matches' | 'partnership';

// Format relative time
const formatRelativeTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// Format message time for chat
const formatMessageTime = (dateStr: string): string => {
  const date = new Date(dateStr);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const Partners: React.FC = () => {
  const { user } = useAuth();

  // Core state
  const [activeTab, setActiveTab] = useState<TabView>('profile');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Profile state
  const [profile, setProfile] = useState<PartnerProfileDto | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);

  // Profile form state
  const [formDiscipline, setFormDiscipline] = useState('');
  const [formGoals, setFormGoals] = useState<string[]>([]);
  const [formTimezone, setFormTimezone] = useState('');
  const [formCommStyle, setFormCommStyle] = useState('');
  const [formFrequency, setFormFrequency] = useState('');
  const [formExperience, setFormExperience] = useState('');
  const [formBio, setFormBio] = useState('');

  // Match state
  const [matches, setMatches] = useState<MatchSuggestionDto[]>([]);
  const [isLoadingMatches, setIsLoadingMatches] = useState(false);
  const [processingMatchId, setProcessingMatchId] = useState<string | null>(null);

  // Partnership state
  const [partnership, setPartnership] = useState<PartnershipDto | null>(null);
  const [messages, setMessages] = useState<PartnerMessageDto[]>([]);
  const [messageText, setMessageText] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [showEndPartnershipModal, setShowEndPartnershipModal] = useState(false);
  const [endReason, setEndReason] = useState('');
  const [isEndingPartnership, setIsEndingPartnership] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom of messages
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load initial data
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const [profileData, partnershipData] = await Promise.all([
        partnersService.getProfile(),
        partnersService.getActivePartnership(),
      ]);

      setProfile(profileData);
      setPartnership(partnershipData);

      if (profileData && !partnershipData) {
        setActiveTab('matches');
      } else if (partnershipData) {
        setActiveTab('partnership');
      } else {
        setActiveTab('profile');
      }
    } catch (err) {
      console.error('Failed to load partner data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load matches when tab changes to matches
  const loadMatches = useCallback(async () => {
    if (!profile) return;
    try {
      setIsLoadingMatches(true);
      const data = await partnersService.getMatchSuggestions();
      setMatches(data);
    } catch (err) {
      console.error('Failed to load matches:', err);
    } finally {
      setIsLoadingMatches(false);
    }
  }, [profile]);

  useEffect(() => {
    if (activeTab === 'matches' && profile) {
      loadMatches();
    }
  }, [activeTab, profile, loadMatches]);

  // Load messages when tab changes to partnership
  const loadMessages = useCallback(async () => {
    if (!partnership) return;
    try {
      setIsLoadingMessages(true);
      const data = await partnersService.getMessages();
      setMessages(data);
    } catch (err) {
      console.error('Failed to load messages:', err);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [partnership]);

  useEffect(() => {
    if (activeTab === 'partnership' && partnership) {
      loadMessages();
    }
  }, [activeTab, partnership, loadMessages]);

  // Profile form helpers
  const resetFormToProfile = useCallback(() => {
    if (profile) {
      setFormDiscipline(profile.discipline);
      setFormGoals(profile.goals);
      setFormTimezone(profile.timezone);
      setFormCommStyle(profile.communicationStyle);
      setFormFrequency(profile.checkInFrequency);
      setFormExperience(profile.experienceLevel);
      setFormBio(profile.bio);
    }
  }, [profile]);

  const resetFormToEmpty = useCallback(() => {
    setFormDiscipline('');
    setFormGoals([]);
    setFormTimezone('');
    setFormCommStyle('');
    setFormFrequency('');
    setFormExperience('');
    setFormBio('');
  }, []);

  const handleEditProfile = () => {
    resetFormToProfile();
    setIsEditingProfile(true);
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    if (!profile) {
      resetFormToEmpty();
    }
  };

  const toggleGoal = (goal: string) => {
    setFormGoals(prev =>
      prev.includes(goal)
        ? prev.filter(g => g !== goal)
        : [...prev, goal]
    );
  };

  const isFormValid = (): boolean => {
    return !!(
      formDiscipline &&
      formGoals.length > 0 &&
      formTimezone &&
      formCommStyle &&
      formFrequency &&
      formExperience &&
      formBio.trim()
    );
  };

  const handleSaveProfile = async () => {
    if (!isFormValid()) return;

    const request: CreatePartnerProfileRequest = {
      discipline: formDiscipline,
      goals: formGoals,
      timezone: formTimezone,
      communicationStyle: formCommStyle,
      checkInFrequency: formFrequency,
      experienceLevel: formExperience,
      bio: formBio.trim(),
    };

    setIsSavingProfile(true);
    try {
      let savedProfile: PartnerProfileDto;
      if (profile) {
        savedProfile = await partnersService.updateProfile(request);
      } else {
        savedProfile = await partnersService.createProfile(request);
      }
      setProfile(savedProfile);
      setIsEditingProfile(false);
      if (!partnership) {
        setActiveTab('matches');
      }
    } catch (err) {
      console.error('Failed to save profile:', err);
      setError('Failed to save profile. Please try again.');
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Match handlers
  const handleAcceptMatch = async (targetUserId: string) => {
    setProcessingMatchId(targetUserId);
    try {
      const newPartnership = await partnersService.requestMatch(targetUserId);
      setPartnership(newPartnership);
      setActiveTab('partnership');
    } catch (err) {
      console.error('Failed to accept match:', err);
      setError('Failed to send match request. Please try again.');
    } finally {
      setProcessingMatchId(null);
    }
  };

  const handleDeclineMatch = async (targetUserId: string) => {
    setProcessingMatchId(targetUserId);
    try {
      await partnersService.declineMatch(targetUserId);
      setMatches(prev => prev.filter(m => m.profile.userId !== targetUserId));
    } catch (err) {
      console.error('Failed to decline match:', err);
    } finally {
      setProcessingMatchId(null);
    }
  };

  // Message handlers
  const handleSendMessage = async () => {
    if (!messageText.trim() || !partnership) return;

    setIsSendingMessage(true);
    try {
      const newMessage = await partnersService.sendMessage({
        content: messageText.trim(),
      });
      setMessages(prev => [...prev, newMessage]);
      setMessageText('');
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message. Please try again.');
    } finally {
      setIsSendingMessage(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // End partnership
  const handleEndPartnership = async () => {
    setIsEndingPartnership(true);
    try {
      await partnersService.endPartnership(endReason || undefined);
      setPartnership(null);
      setMessages([]);
      setShowEndPartnershipModal(false);
      setEndReason('');
      setActiveTab('matches');
    } catch (err) {
      console.error('Failed to end partnership:', err);
      setError('Failed to end partnership. Please try again.');
    } finally {
      setIsEndingPartnership(false);
    }
  };

  // Get match score color
  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'excellent';
    if (score >= 60) return 'good';
    if (score >= 40) return 'fair';
    return 'low';
  };

  // Render loading
  if (isLoading) {
    return (
      <AppLayout pageTitle="Partners">
        <div className="partners">
          <div className="partners__loading">
            <Loader2 size={32} className="partners__loading-spinner" />
            <p>Loading partner data...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Determine if we show the profile form or profile summary
  const showProfileForm = !profile || isEditingProfile;

  return (
    <AppLayout pageTitle="Partners">
      <div className="partners">
        {/* Ambient background */}
        <div className="partners__ambient">
          <div className="partners__ambient-shape partners__ambient-shape--1" />
          <div className="partners__ambient-shape partners__ambient-shape--2" />
        </div>

        <main className="partners__main">
          {/* Intro */}
          <div className="partners__intro">
            <p>Find your creative accountability partner. Grow together.</p>
          </div>

          {/* Error banner */}
          {error && (
            <div className="partners__error-banner">
              <AlertTriangle size={16} />
              <span>{error}</span>
              <button onClick={() => setError(null)}>
                <X size={14} />
              </button>
            </div>
          )}

          {/* Tabs */}
          <div className="partners__tabs">
            <button
              className={`partners__tab ${activeTab === 'profile' ? 'partners__tab--active' : ''}`}
              onClick={() => setActiveTab('profile')}
            >
              <UserPlus size={16} />
              <span>Profile</span>
            </button>
            <button
              className={`partners__tab ${activeTab === 'matches' ? 'partners__tab--active' : ''}`}
              onClick={() => setActiveTab('matches')}
              disabled={!profile}
            >
              <Star size={16} />
              <span>Matches</span>
            </button>
            <button
              className={`partners__tab ${activeTab === 'partnership' ? 'partners__tab--active' : ''}`}
              onClick={() => setActiveTab('partnership')}
              disabled={!partnership}
            >
              <Users size={16} />
              <span>Partner</span>
              {partnership && (
                <span className="partners__tab-badge">
                  <Flame size={12} />
                  {partnership.checkInStreak}
                </span>
              )}
            </button>
          </div>

          {/* Tab Content */}
          <div className="partners__content">
            {/* ==================== PROFILE TAB ==================== */}
            {activeTab === 'profile' && (
              <div className="partners__profile-tab">
                {showProfileForm ? (
                  <div className="partners__profile-form">
                    <h3 className="partners__section-title">
                      {profile ? 'Edit Your Partner Profile' : 'Create Your Partner Profile'}
                    </h3>
                    <p className="partners__section-desc">
                      Tell potential partners about yourself and what you're looking for.
                    </p>

                    <div className="partners__form-fields">
                      <Select
                        label="Creative Discipline"
                        options={disciplineOptions}
                        value={formDiscipline}
                        onChange={(e) => setFormDiscipline(e.target.value)}
                        placeholder="Select your discipline"
                      />

                      <div className="partners__form-group">
                        <label className="partners__form-label">Creative Goals</label>
                        <p className="partners__form-hint">Select one or more goals</p>
                        <div className="partners__goals-grid">
                          {goalOptions.map(goal => (
                            <button
                              key={goal}
                              className={`partners__goal-chip ${formGoals.includes(goal) ? 'partners__goal-chip--active' : ''}`}
                              onClick={() => toggleGoal(goal)}
                              type="button"
                            >
                              {goal}
                              {formGoals.includes(goal) && <Check size={12} />}
                            </button>
                          ))}
                        </div>
                      </div>

                      <Select
                        label="Timezone"
                        options={timezoneOptions}
                        value={formTimezone}
                        onChange={(e) => setFormTimezone(e.target.value)}
                        placeholder="Select your timezone"
                      />

                      <Select
                        label="Communication Style"
                        options={communicationStyleOptions}
                        value={formCommStyle}
                        onChange={(e) => setFormCommStyle(e.target.value)}
                        placeholder="How do you prefer to communicate?"
                      />

                      <Select
                        label="Check-in Frequency"
                        options={checkInFrequencyOptions}
                        value={formFrequency}
                        onChange={(e) => setFormFrequency(e.target.value)}
                        placeholder="How often would you like to check in?"
                      />

                      <Select
                        label="Experience Level"
                        options={experienceLevelOptions}
                        value={formExperience}
                        onChange={(e) => setFormExperience(e.target.value)}
                        placeholder="Select your experience level"
                      />

                      <div className="partners__form-group">
                        <Textarea
                          label="About You"
                          placeholder="Tell potential partners about your creative journey, what inspires you, and what kind of partnership you're looking for..."
                          value={formBio}
                          onChange={(e) => setFormBio(e.target.value)}
                          rows={4}
                        />
                      </div>
                    </div>

                    <div className="partners__form-actions">
                      {profile && (
                        <Button variant="ghost" onClick={handleCancelEdit}>
                          Cancel
                        </Button>
                      )}
                      <Button
                        onClick={handleSaveProfile}
                        disabled={isSavingProfile || !isFormValid()}
                      >
                        {isSavingProfile ? (
                          <>
                            <Loader2 size={16} className="partners__loading-spinner" />
                            Saving...
                          </>
                        ) : profile ? 'Update Profile' : 'Create Profile'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="partners__profile-summary">
                    <div className="partners__profile-header">
                      <h3 className="partners__section-title">Your Partner Profile</h3>
                      <Button variant="ghost" onClick={handleEditProfile}>
                        <Edit3 size={16} />
                        Edit
                      </Button>
                    </div>

                    <Card variant="default" padding="lg" className="partners__profile-card">
                      <div className="partners__profile-details">
                        <div className="partners__profile-detail">
                          <Target size={16} />
                          <div>
                            <span className="partners__detail-label">Discipline</span>
                            <span className="partners__detail-value">
                              {disciplineOptions.find(d => d.value === profile.discipline)?.label || profile.discipline}
                            </span>
                          </div>
                        </div>

                        <div className="partners__profile-detail">
                          <Zap size={16} />
                          <div>
                            <span className="partners__detail-label">Experience</span>
                            <span className="partners__detail-value">
                              {experienceLevelOptions.find(e => e.value === profile.experienceLevel)?.label || profile.experienceLevel}
                            </span>
                          </div>
                        </div>

                        <div className="partners__profile-detail">
                          <Globe size={16} />
                          <div>
                            <span className="partners__detail-label">Timezone</span>
                            <span className="partners__detail-value">
                              {timezoneOptions.find(t => t.value === profile.timezone)?.label || profile.timezone}
                            </span>
                          </div>
                        </div>

                        <div className="partners__profile-detail">
                          <MessageCircle size={16} />
                          <div>
                            <span className="partners__detail-label">Communication</span>
                            <span className="partners__detail-value">
                              {communicationStyleOptions.find(c => c.value === profile.communicationStyle)?.label || profile.communicationStyle}
                            </span>
                          </div>
                        </div>

                        <div className="partners__profile-detail">
                          <Clock size={16} />
                          <div>
                            <span className="partners__detail-label">Check-in Frequency</span>
                            <span className="partners__detail-value">
                              {checkInFrequencyOptions.find(f => f.value === profile.checkInFrequency)?.label || profile.checkInFrequency}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="partners__profile-goals">
                        <span className="partners__detail-label">Goals</span>
                        <div className="partners__goals-list">
                          {profile.goals.map(goal => (
                            <span key={goal} className="partners__goal-tag">{goal}</span>
                          ))}
                        </div>
                      </div>

                      <div className="partners__profile-bio">
                        <span className="partners__detail-label">Bio</span>
                        <p>{profile.bio}</p>
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            )}

            {/* ==================== MATCHES TAB ==================== */}
            {activeTab === 'matches' && (
              <div className="partners__matches-tab">
                {!profile ? (
                  <EmptyState
                    title="Create your profile first"
                    message="Set up your partner profile to start finding creative matches."
                    icon={<UserPlus size={48} />}
                    action={{
                      label: 'Create Profile',
                      onClick: () => setActiveTab('profile'),
                    }}
                  />
                ) : isLoadingMatches ? (
                  <div className="partners__loading">
                    <Loader2 size={32} className="partners__loading-spinner" />
                    <p>Finding your matches...</p>
                  </div>
                ) : matches.length === 0 ? (
                  <EmptyState
                    title="No matches yet"
                    message="We're looking for creative partners that match your profile. Check back soon!"
                    icon={<Heart size={48} />}
                    action={{
                      label: 'Refresh',
                      onClick: loadMatches,
                      variant: 'secondary',
                    }}
                  />
                ) : (
                  <div className="partners__matches-list">
                    <h3 className="partners__section-title">Suggested Partners</h3>
                    <p className="partners__section-desc">
                      Based on your profile, we found {matches.length} potential partner{matches.length !== 1 ? 's' : ''}.
                    </p>

                    {matches.map((match, index) => (
                      <Card
                        key={match.profile.userId}
                        variant="default"
                        padding="md"
                        className="partners__match-card"
                        style={{ animationDelay: `${index * 0.08}s` }}
                      >
                        <div className="partners__match-header">
                          <div className="partners__match-avatar">
                            <Users size={20} />
                          </div>
                          <div className="partners__match-info">
                            <span className="partners__match-discipline">
                              {disciplineOptions.find(d => d.value === match.profile.discipline)?.label || match.profile.discipline}
                            </span>
                            <span className="partners__match-level">
                              {experienceLevelOptions.find(e => e.value === match.profile.experienceLevel)?.label || match.profile.experienceLevel}
                            </span>
                          </div>
                          <div className={`partners__match-score partners__match-score--${getScoreColor(match.matchScore)}`}>
                            <div className="partners__score-ring">
                              <svg viewBox="0 0 36 36" className="partners__score-svg">
                                <path
                                  d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  strokeDasharray={`${match.matchScore}, 100`}
                                  opacity="0.3"
                                />
                                <path
                                  d="M18 2.0845
                                    a 15.9155 15.9155 0 0 1 0 31.831
                                    a 15.9155 15.9155 0 0 1 0 -31.831"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="3"
                                  strokeDasharray={`${match.matchScore}, 100`}
                                />
                              </svg>
                              <span className="partners__score-value">{match.matchScore}%</span>
                            </div>
                          </div>
                        </div>

                        <p className="partners__match-reason">
                          <Star size={14} />
                          {match.matchReason}
                        </p>

                        <div className="partners__match-goals">
                          {match.profile.goals.slice(0, 3).map(goal => (
                            <span key={goal} className="partners__goal-tag">{goal}</span>
                          ))}
                          {match.profile.goals.length > 3 && (
                            <span className="partners__goal-tag partners__goal-tag--more">
                              +{match.profile.goals.length - 3}
                            </span>
                          )}
                        </div>

                        {match.profile.bio && (
                          <p className="partners__match-bio">{match.profile.bio}</p>
                        )}

                        <div className="partners__match-meta">
                          <span>
                            <Globe size={12} />
                            {timezoneOptions.find(t => t.value === match.profile.timezone)?.label || match.profile.timezone}
                          </span>
                          <span>
                            <Clock size={12} />
                            {checkInFrequencyOptions.find(f => f.value === match.profile.checkInFrequency)?.label || match.profile.checkInFrequency}
                          </span>
                        </div>

                        <div className="partners__match-actions">
                          <Button
                            variant="ghost"
                            onClick={() => handleDeclineMatch(match.profile.userId)}
                            disabled={processingMatchId === match.profile.userId}
                          >
                            <X size={16} />
                            Pass
                          </Button>
                          <Button
                            onClick={() => handleAcceptMatch(match.profile.userId)}
                            disabled={processingMatchId === match.profile.userId}
                          >
                            {processingMatchId === match.profile.userId ? (
                              <Loader2 size={16} className="partners__loading-spinner" />
                            ) : (
                              <Heart size={16} />
                            )}
                            Connect
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ==================== PARTNERSHIP TAB ==================== */}
            {activeTab === 'partnership' && (
              <div className="partners__partnership-tab">
                {!partnership ? (
                  <EmptyState
                    title="No active partnership"
                    message="Browse match suggestions to find your accountability partner."
                    icon={<Users size={48} />}
                    action={{
                      label: 'Find Partners',
                      onClick: () => setActiveTab('matches'),
                    }}
                  />
                ) : (
                  <>
                    {/* Partner info header */}
                    <Card variant="default" padding="md" className="partners__partner-info">
                      <div className="partners__partner-header">
                        <div className="partners__partner-avatar">
                          <Users size={24} />
                        </div>
                        <div className="partners__partner-details">
                          <span className="partners__partner-discipline">
                            {partnership.partnerProfile
                              ? (disciplineOptions.find(d => d.value === partnership.partnerProfile!.discipline)?.label || partnership.partnerProfile.discipline)
                              : 'Creative Partner'}
                          </span>
                          <span className="partners__partner-since">
                            Partners since {new Date(partnership.startedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <button
                          className="partners__partner-end"
                          onClick={() => setShowEndPartnershipModal(true)}
                          title="End partnership"
                        >
                          <UserX size={18} />
                        </button>
                      </div>

                      <div className="partners__partner-stats">
                        <div className="partners__stat">
                          <Flame size={18} className="partners__stat-icon partners__stat-icon--streak" />
                          <span className="partners__stat-value">{partnership.checkInStreak}</span>
                          <span className="partners__stat-label">day streak</span>
                        </div>
                        <div className="partners__stat">
                          <Clock size={18} className="partners__stat-icon" />
                          <span className="partners__stat-value">
                            {partnership.lastCheckInAt
                              ? formatRelativeTime(partnership.lastCheckInAt)
                              : 'Never'}
                          </span>
                          <span className="partners__stat-label">last check-in</span>
                        </div>
                        <div className="partners__stat">
                          <Star size={18} className="partners__stat-icon partners__stat-icon--score" />
                          <span className="partners__stat-value">{partnership.matchScore}%</span>
                          <span className="partners__stat-label">match</span>
                        </div>
                      </div>
                    </Card>

                    {/* Messages */}
                    <div className="partners__messages">
                      <div className="partners__messages-list">
                        {isLoadingMessages ? (
                          <div className="partners__loading partners__loading--compact">
                            <Loader2 size={24} className="partners__loading-spinner" />
                            <p>Loading messages...</p>
                          </div>
                        ) : messages.length === 0 ? (
                          <div className="partners__messages-empty">
                            <MessageCircle size={32} />
                            <p>No messages yet. Say hello to your partner!</p>
                          </div>
                        ) : (
                          messages.map(msg => (
                            <div
                              key={msg.id}
                              className={`partners__message ${msg.senderId === user?.id ? 'partners__message--sent' : 'partners__message--received'}`}
                            >
                              <div className="partners__message-bubble">
                                <p>{msg.content}</p>
                                <span className="partners__message-time">
                                  {formatMessageTime(msg.createdAt)}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                        <div ref={messagesEndRef} />
                      </div>

                      {/* Message input */}
                      <div className="partners__message-input">
                        <input
                          type="text"
                          placeholder="Type a message..."
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          onKeyDown={handleKeyDown}
                          disabled={isSendingMessage}
                        />
                        <button
                          className="partners__send-btn"
                          onClick={handleSendMessage}
                          disabled={isSendingMessage || !messageText.trim()}
                        >
                          {isSendingMessage ? (
                            <Loader2 size={18} className="partners__loading-spinner" />
                          ) : (
                            <Send size={18} />
                          )}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </main>

        {/* End Partnership Modal */}
        <Modal
          isOpen={showEndPartnershipModal}
          onClose={() => setShowEndPartnershipModal(false)}
          title="End Partnership"
          size="sm"
        >
          <div className="partners__end-modal">
            <p className="partners__end-warning">
              Are you sure you want to end this partnership? This action cannot be undone.
            </p>
            <Textarea
              placeholder="Reason for ending (optional)"
              value={endReason}
              onChange={(e) => setEndReason(e.target.value)}
              rows={3}
            />
            <div className="partners__end-actions">
              <Button variant="ghost" onClick={() => setShowEndPartnershipModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleEndPartnership}
                disabled={isEndingPartnership}
                className="partners__end-confirm"
              >
                {isEndingPartnership ? (
                  <>
                    <Loader2 size={16} className="partners__loading-spinner" />
                    Ending...
                  </>
                ) : (
                  <>
                    <UserX size={16} />
                    End Partnership
                  </>
                )}
              </Button>
            </div>
          </div>
        </Modal>
      </div>
    </AppLayout>
  );
};

export default Partners;
